import React, { useState, useEffect } from "react";
import {
  Modal,
  Tree,
  Button,
  Space,
  message,
  Spin,
  Alert,
  Tabs,
} from "antd";
import { useTranslation } from "react-i18next";
import { roleService } from "../../services/roleService";

/**
 * Role PDA Menu Authorization Modal Component
 * @author AI Assistant
 * @description Modal component for managing role menu permissions for WMS_PDA domain with tree selection
 */
const RolePdaMenuAuthModal = ({ visible, role, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menuTreeData, setMenuTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [originalCheckedKeys, setOriginalCheckedKeys] = useState([]);

  // Load menu data and role permissions when modal opens
  useEffect(() => {
    if (visible && role) {
      loadMenuData();
      loadRolePermissions();
    }
  }, [visible, role]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      resetState();
    }
  }, [visible]);

  const resetState = () => {
    setMenuTreeData([]);
    setCheckedKeys([]);
    setOriginalCheckedKeys([]);
    setExpandedKeys([]);
    setAutoExpandParent(true);
    setLoading(false);
    setSaving(false);
  };

  const loadMenuData = async () => {
    setLoading(true);
    try {
      // 获取WMS_PDA领域的菜单数据
      const response = await roleService.getAvailableMenus("WMS_PDA");
      if (response.success && response.data) {
        const treeData = convertMenuToTreeData(response.data);
        setMenuTreeData(treeData);

        // Auto expand all parent nodes
        const allKeys = extractAllKeys(treeData);
        setExpandedKeys(allKeys);
      } else {
        message.error(response.errorMessage || t("role.loadMenusFailed"));
      }
    } catch (error) {
      console.error("加载PDA菜单数据失败:", error);
      message.error(t("role.loadMenusFailedRetry"));
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async () => {
    if (!role?.code) return;

    try {
      // 获取角色已授权的PDA菜单
      const response = await roleService.getAuthedMenu(role?.code, "WMS_PDA");
      if (response.success && response.data) {
        // 从返回数据中提取已授权的菜单code
        const authedMenuCodes = extractMenuCodesFromResponse(response.data);
        setCheckedKeys(authedMenuCodes);
        setOriginalCheckedKeys([...authedMenuCodes]); // 保存原始状态用于比较
      } else {
        // 回退到从角色数据中获取菜单权限
        const menuCodes = Array.isArray(role.pdaMenuCodes) ? role.pdaMenuCodes : [];
        setCheckedKeys(menuCodes);
        setOriginalCheckedKeys([...menuCodes]); // 保存原始状态用于比较
      }
    } catch (error) {
      console.error("加载角色PDA权限失败:", error);
      // 回退到从角色数据中获取菜单权限
      try {
        const menuCodes = Array.isArray(role.pdaMenuCodes) ? role.pdaMenuCodes : [];
        setCheckedKeys(menuCodes);
        setOriginalCheckedKeys([...menuCodes]); // 保存原始状态用于比较
      } catch (fallbackError) {
        console.error("回退加载角色PDA权限失败:", fallbackError);
        message.error(t("role.loadPermissionsFailed"));
      }
    }
  };

  // 从响应数据中提取菜单code
  const extractMenuCodesFromResponse = (data) => {
    const codes = [];

    // 递归提取所有菜单code
    const extract = (nodes) => {
      if (!nodes || !Array.isArray(nodes)) return;

      nodes.forEach((node) => {
        if (node.code && node.roleHasAuthed) {
          codes.push(node.code);
        }
        if (node.children && Array.isArray(node.children)) {
          extract(node.children);
        }
      });
    };

    // 处理响应数据结构
    if (data.tree && Array.isArray(data.tree)) {
      extract(data.tree);
    } else if (Array.isArray(data)) {
      extract(data);
    } else if (data.children && Array.isArray(data.children)) {
      extract([data]);
    }

    return codes;
  };

  // Convert menu data to Ant Design Tree format
  const convertMenuToTreeData = (menuRoot) => {
    if (!menuRoot || !menuRoot.children) {
      return [];
    }

    const convertNode = (node) => {
      const treeNode = {
        title: node.name,
        key: node.code,
        children: node.children ? node.children.map(convertNode) : undefined,
      };
      return treeNode;
    };

    return menuRoot.children.map(convertNode);
  };

  // Extract all keys from tree data for auto-expansion
  const extractAllKeys = (treeData) => {
    const keys = [];
    const extract = (nodes) => {
      nodes.forEach((node) => {
        keys.push(node.key);
        if (node.children) {
          extract(node.children);
        }
      });
    };
    if (Array.isArray(treeData)) {
      extract(treeData);
    }
    return keys;
  };

  // Handle tree check change
  const onCheck = (checkedKeysValue, e) => {
    setCheckedKeys(checkedKeysValue);
  };

  // Handle tree expand
  const onExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
    setAutoExpandParent(false);
  };

  // 构建父子关系映射
  const buildParentMap = (treeData) => {
    const parentMap = new Map();

    const traverse = (nodes, parentKey = null) => {
      nodes.forEach((node) => {
        if (parentKey) {
          parentMap.set(node.key, parentKey);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children, node.key);
        }
      });
    };

    traverse(treeData);
    return parentMap;
  };

  // 确保所有父节点都被选中
  const ensureParentKeysIncluded = (checkedKeys) => {
    const parentMap = buildParentMap(menuTreeData);
    
    // 类型判断：如果是数组则直接使用，如果是对象则使用checked属性
    const checkedArray = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys?.checked || [];
    
    const resultKeys = new Set(checkedArray);

    // 对于每个选中的节点，将其所有祖先节点都加入结果中
    checkedArray.forEach((key) => {
      let currentKey = key;
      while (parentMap.has(currentKey)) {
        const parentKey = parentMap.get(currentKey);
        resultKeys.add(parentKey);
        currentKey = parentKey;
      }
    });

    return Array.from(resultKeys);
  };

  // Select all menus
  const selectAll = () => {
    const allKeys = extractAllKeys(menuTreeData);
    setCheckedKeys(allKeys);
  };

  // Clear all selections
  const clearAll = () => {
    setCheckedKeys([]);
  };

  // Check if permissions have changed
  const hasChanges = () => {
    if (checkedKeys.length !== originalCheckedKeys.length) return true;

    const sortedChecked = [...checkedKeys].sort();
    const sortedOriginal = [...originalCheckedKeys].sort();

    return sortedChecked.some((key, index) => key !== sortedOriginal[index]);
  };

  // Save menu permissions
  const handleSave = async () => {
    if (!role?.code) return;

    if (!hasChanges()) {
      message.info(t("role.noChangesToSave"));
      return;
    }

    setSaving(true);
    try {
      // 确保所有父节点都被包含在保存的数据中
      const keysToSave = ensureParentKeysIncluded(checkedKeys);
      const response = await roleService.updateRoleMenuPermissions(
        role.code,
        keysToSave
      );
      if (response.success) {
        message.success(t("role.updateMenuPermissionsSuccess"));
        onSuccess && onSuccess();
        onCancel();
      } else {
        message.error(
          response.errorMessage || t("role.updateMenuPermissionsFailed")
        );
      }
    } catch (error) {
      console.error("保存PDA菜单权限失败:", error);
      message.error(t("role.updateMenuPermissionsFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      Modal.confirm({
        title: t("role.unsavedChangesTitle"),
        content: t("role.unsavedChangesContent"),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: () => {
          resetState();
          onCancel();
        },
      });
    } else {
      resetState();
      onCancel();
    }
  };

  return (
    <Modal
      title={t("role.pdaMenuAuthorizationTitle", { roleName: role?.name || "" })}
      open={visible}
      onCancel={handleCancel}
      width={800}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("common.cancel")}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
          disabled={loading || !hasChanges()}
        >
          {t("common.save")}
        </Button>,
      ]}
    >
      {/* 操作按钮区域 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button size="small" onClick={selectAll} disabled={loading || saving}>
            {t("role.selectAll")}
          </Button>
          <Button size="small" onClick={clearAll} disabled={loading || saving}>
            {t("role.clearAll")}
          </Button>
        </Space>
      </div>

      {/* 权限变更提示 */}
      {hasChanges() && (
        <Alert
          message={t("role.permissionsChanged")}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          closable
        />
      )}

      {/* 菜单树区域 */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" tip={t("role.loadingMenus")} />
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            padding: "16px",
            maxHeight: "400px",
            overflowY: "auto",
            backgroundColor: "#fafafa",
          }}
        >
          {menuTreeData.length > 0 ? (
            <>
              <Tree
                checkable
                checkStrictly={true}
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onCheck={onCheck}
                checkedKeys={checkedKeys}
                treeData={menuTreeData}
                disabled={loading || saving}
                style={{ backgroundColor: "transparent" }}
              />

              {/* 统计信息 */}
              <div
                style={{
                  marginTop: "16px",
                  padding: "8px 12px",
                  backgroundColor: "#e6f7ff",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#1890ff",
                  textAlign: "center",
                  borderLeft: "3px solid #1890ff",
                }}
              >
                {t("role.selectedMenuCount", { count: checkedKeys.length })} /{" "}
                {t("role.totalMenuCount", {
                  count: extractAllKeys(menuTreeData).length,
                })}
              </div>
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "#999",
              }}
            >
              {t("role.noMenusAvailable")}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default RolePdaMenuAuthModal;